import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { TenantService } from './tenant.service';
import { getLoadResourceURL, getUploadProcessURL, getUploadRevertURL } from '../util/const';
import { FilePondServerConfigProps } from 'filepond';

export interface FilePondServerConfigOptions {
  allowRevert?: boolean;
  load?: ((source: any, load: (file: File | Blob) => void) => void) | boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FilePondConfigService {
  private authService = inject(AuthService);
  private tenantService = inject(TenantService);

  /**
   * Get common upload headers including Bearer token and Tenant headers.
   */
  async getHeaders(): Promise<Record<string, string>> {
    const token = await this.authService.getToken();
    const slug = this.tenantService.getTenantSlug();
    const tenantId = this.tenantService.getTenantId();

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (slug) {
      headers['X-Tenant-Slug'] = slug;
    }
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }

  /**
   * Builds the FilePond `server` config object with auth token and tenant headers attached.
   */
  async getServerConfig(options: FilePondServerConfigOptions = {}): Promise<FilePondServerConfigProps['server']> {
    const headers = await this.getHeaders();

    let loadHandler: ((source: any, load: (file: File | Blob) => void) => void) | null = null;
    if (typeof options.load === 'function') {
      loadHandler = options.load;
    } else if (options.load === true) {
      loadHandler = this.getDefaultLoadHandler(headers);
    }

    return {
      headers,
      process: {
        url: getUploadProcessURL(),
        headers,
      },
      revert: options.allowRevert
        ? {
            url: getUploadRevertURL(),
            headers,
          }
        : null,
      fetch: null,
      restore: null,
      ...(loadHandler ? { load: loadHandler } : {}),
    };
  }

  /**
   * Default load handler for previewing existing uploaded resources.
   */
  getDefaultLoadHandler(headers?: Record<string, string>) {
    return (source: any, load: (file: File | Blob) => void) => {
      fetch(getLoadResourceURL() + '?source=' + encodeURIComponent(source), {
        headers: headers || {},
      })
        .then((res) => res.blob())
        .then((blob) => {
          const fileName =
            (typeof source === 'string' ? source.split('/').pop() : '') || 'file';
          const file = new File([blob], fileName, {
            type: blob.type,
          });
          load(file);
        })
        .catch((err) => {
          console.error('Failed to load filepond resource preview', err);
        });
    };
  }
}
